import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const CLAIM_REASONS = [
  { code: "KEIN_GEWERBE", label: "Kein Gewerbe" },
  { code: "DIREKTER_WETTBEWERBER", label: "Direkter Wettbewerber" },
  { code: "UNTERNEHMEN_EXISTIERT_NICHT", label: "Unternehmen existiert nicht mehr" },
  { code: "VERWEIGERTER_KONTAKT", label: "Verweigerter Kontakt" },
  { code: "TELEFONNUMMER_UNBEKANNT", label: "Telefonnummer unbekannt" },
  { code: "FALSCHE_KATEGORIE", label: "Falsche Kategorie" },
] as const;

interface ClaimDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (reasonCode: string, reasonText: string) => Promise<void>;
  isLoading?: boolean;
}

const ClaimDialog = ({ open, onOpenChange, onSubmit, isLoading }: ClaimDialogProps) => {
  const { t } = useTranslation();
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [reasonText, setReasonText] = useState("");

  const handleSubmit = async () => {
    if (!selectedReason) return;
    await onSubmit(selectedReason, reasonText);
    setSelectedReason(null);
    setReasonText("");
  };

  const handleOpenChange = (val: boolean) => {
    if (!val) {
      setSelectedReason(null);
      setReasonText("");
    }
    onOpenChange(val);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("dashboard.claimTitle", "Reklamation einreichen")}</DialogTitle>
          <DialogDescription>
            {t("dashboard.claimDescription", "Wählen Sie den Grund für die Reklamation.")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 mt-2">
          {CLAIM_REASONS.map((reason) => (
            <button
              key={reason.code}
              onClick={() => setSelectedReason(reason.code)}
              className={cn(
                "w-full text-left px-4 py-3 border text-sm transition-colors",
                selectedReason === reason.code
                  ? "border-accent bg-accent/5 text-foreground"
                  : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/20"
              )}
            >
              {reason.label}
            </button>
          ))}
        </div>

        <div className="mt-4 space-y-2">
          <label className="text-micro block">
            {t("dashboard.claimComment", "Kommentar (optional)")}
          </label>
          <textarea
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value)}
            className="w-full min-h-[80px] p-3 border border-border bg-background text-sm resize-none focus:outline-none focus:border-primary"
            placeholder={t("dashboard.claimCommentPlaceholder", "Weitere Details...")}
            maxLength={1000}
          />
        </div>

        <div className="flex gap-3 mt-4">
          <Button
            variant="commit"
            className="flex-1"
            onClick={handleSubmit}
            disabled={!selectedReason || isLoading}
          >
            {isLoading
              ? t("common.loading", "Wird gesendet...")
              : t("dashboard.claimSubmit", "Reklamation einreichen")}
          </Button>
          <Button variant="quiet" onClick={() => handleOpenChange(false)}>
            {t("common.cancel", "Abbrechen")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ClaimDialog;
